import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { motion } from 'framer-motion';

import { BACKEND_URL } from '../config';
import { fetchWithOrg } from '../utils/api';
import { useOrganization } from '../context/OrganizationContext';

import {
  CircleNotch,
  DownloadSimple,
  FileText,
  Funnel,
  MagnifyingGlass,
  Pulse,
  TrendUp
} from '@phosphor-icons/react';
const AuditLogs = () => {
  const { activeOrganization } = useOrganization();
  
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [targetTypeFilter, setTargetTypeFilter] = useState('');
  const [currentUserRole, setCurrentUserRole] = useState(null);

  useEffect(() => {
    if (activeOrganization) {
checkRole();
    }
  }, [activeOrganization]);

  const checkRole = async () => {
    try {
const roleRes = await fetchWithOrg(
  `${BACKEND_URL}/api/team/my-role`,
  {},
  activeOrganization.organization_id
);

if (roleRes.ok) {
  const roleData = await roleRes.json();
  setCurrentUserRole(roleData.role);
  
  const isAdmin = roleData.role === 'admin' || roleData.role === 'primary_admin';
  if (isAdmin) {
    loadData();
  } else {
    setLoading(false);
  }
}
    } catch (err) {
console.error('Error checking role:', err);
setLoading(false);
    }
  };

  const loadData = async () => {
    try {
setLoading(true);

// Load logs
const logsRes = await fetchWithOrg(
  `${BACKEND_URL}/api/audit/logs?limit=100`,
  {},
  activeOrganization.organization_id
);

if (logsRes.ok) {
  const logsData = await logsRes.json();
  setLogs(logsData);
}

// Load stats
const statsRes = await fetchWithOrg(
  `${BACKEND_URL}/api/audit/stats`,
  {},
  activeOrganization.organization_id
);

if (statsRes.ok) {
  const statsData = await statsRes.json();
  setStats(statsData);
}
    } catch (err) {
console.error('Error loading audit logs:', err);
    } finally {
setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', { 
year: 'numeric', 
month: 'short', 
day: 'numeric',
hour: '2-digit',
minute: '2-digit',
second: '2-digit'
    });
  };

  const getActionColor = (action) => {
    if (action.includes('created') || action.includes('joined')) return 'text-[#14F195]';
    if (action.includes('deleted') || action.includes('removed')) return 'text-[#EF4444]';
    if (action.includes('updated') || action.includes('changed')) return 'text-[#F59E0B]';
    return 'text-[#6366F1]';
  };

  const getActionIcon = (action) => {
    if (action.includes('created') || action.includes('joined')) return '✨';
    if (action.includes('deleted') || action.includes('removed')) return '🗑️';
    if (action.includes('updated') || action.includes('changed')) return '✏️';
    if (action.includes('invited')) return '📧';
    return '📝';
  };

  const filteredLogs = logs.filter(log => {
    const matchesSearch = !searchTerm || 
log.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
log.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
log.user_email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesAction = !actionFilter || log.action.includes(actionFilter);
    const matchesTargetType = !targetTypeFilter || log.target_type === targetTypeFilter;
    
    return matchesSearch && matchesAction && matchesTargetType;
  });

  if (loading) {
    return (
<div className="min-h-screen bg-[#161B26] flex items-center justify-center">
  <CircleNotch size={48} className="text-[#6366F1] animate-spin" />
</div>
    );
  }

  if (currentUserRole !== 'admin' && currentUserRole !== 'primary_admin') {
    return (
<div className="min-h-screen bg-[#161B26]">
  <Header />
  <div className="pt-32 pb-20">
    <div className="max-w-6xl mx-auto px-6 text-center">
<FileText size={64} className="text-[#6366F1] mx-auto mb-4" />
<h1 className="text-4xl font-bold text-white mb-4">Access Denied</h1>
<p className="text-[#A0AEC0] text-lg">
  Only administrators can view audit logs.
</p>
    </div>
  </div>
  
</div>
    );
  }

  return (
      <>
<div className="max-w-7xl mx-auto">
    {/* Header */}
    <div className="mb-8">
<div className="flex items-center gap-3 mb-2">
  <FileText size={32} className="text-[#6366F1]" />
  <h1 className="text-4xl sm:text-5xl font-bold text-white">Audit Logs</h1>
</div>
<p className="text-[#A0AEC0] text-base sm:text-lg">
  Complete history of actions in your organization
</p>
    </div>

    {/* Stats Cards */}
    {stats && (
<div className="grid md:grid-cols-3 gap-6 mb-8">
  {/* Total Logs */}
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="glass rounded-2xl p-6"
  >
    <div className="flex items-center justify-between mb-2">
<Pulse size={24} className="text-[#6366F1]" />
<TrendUp size={20} className="text-[#14F195]" />
    </div>
    <h3 className="text-2xl font-bold text-white mb-1">{stats.total_logs}</h3>
    <p className="text-[#A0AEC0] text-sm">Total Events</p>
  </motion.div>

  {/* Top Action */}
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.1 }}
    className="glass rounded-2xl p-6"
  >
    <div className="flex items-center justify-between mb-2">
<span className="text-2xl">{getActionIcon(stats.top_actions[0]?._id || '')}</span>
<span className="text-[#F59E0B] font-mono text-sm">{stats.top_actions[0]?.count || 0}</span>
    </div>
    <h3 className="text-white font-semibold mb-1 truncate">
{stats.top_actions[0]?._id || 'No actions yet'}
    </h3>
    <p className="text-[#A0AEC0] text-sm">Most Common Action</p>
  </motion.div>

  {/* Active Users */}
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.2 }}
    className="glass rounded-2xl p-6"
  >
    <div className="flex items-center justify-between mb-2">
<span className="text-2xl">👥</span>
<span className="text-[#EC4899] font-mono text-sm">{stats.top_users?.length || 0}</span>
    </div>
    <h3 className="text-white font-semibold mb-1">
{stats.top_users[0]?.user_name || 'N/A'}
    </h3>
    <p className="text-[#A0AEC0] text-sm">Most Active User</p>
  </motion.div>
</div>
    )}

    {/* Filters */}
    <div className="glass rounded-2xl p-6 mb-6">
<div className="grid md:grid-cols-3 gap-4">
  {/* MagnifyingGlass */}
  <div className="relative">
    <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A0AEC0]" />
    <input
type="text"
value={searchTerm}
onChange={(e) => setSearchTerm(e.target.value)}
placeholder="Search logs..."
className="w-full pl-10 pr-4 py-3 rounded-xl glass text-white bg-[#1A1B23] border border-[rgba(255,255,255,0.1)] focus:border-[#6366F1] focus:outline-none"
    />
  </div>

  {/* Action Funnel */}
  <div className="relative">
    <Funnel size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A0AEC0]" />
    <select
value={actionFilter}
onChange={(e) => setActionFilter(e.target.value)}
className="w-full pl-10 pr-4 py-3 rounded-xl glass text-white bg-[#1A1B23] border border-[rgba(255,255,255,0.1)] focus:border-[#6366F1] focus:outline-none appearance-none"
    >
<option value="">All Actions</option>
<option value="team">Team</option>
<option value="agent">Agent</option>
<option value="oauth">OAuth</option>
<option value="billing">Billing</option>
    </select>
  </div>

  {/* Target Type Funnel */}
  <div className="relative">
    <Funnel size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A0AEC0]" />
    <select
value={targetTypeFilter}
onChange={(e) => setTargetTypeFilter(e.target.value)}
className="w-full pl-10 pr-4 py-3 rounded-xl glass text-white bg-[#1A1B23] border border-[rgba(255,255,255,0.1)] focus:border-[#6366F1] focus:outline-none appearance-none"
    >
<option value="">All Types</option>
<option value="member">Member</option>
<option value="invitation">Invitation</option>
<option value="agent">Agent</option>
<option value="integration">Integration</option>
    </select>
  </div>
</div>
    </div>

    {/* Logs Table */}
    <div className="glass rounded-2xl overflow-hidden">
<div className="overflow-x-auto">
  <table className="w-full">
    <thead className="bg-[rgba(255,255,255,0.05)]">
<tr>
  <th className="px-6 py-4 text-left text-[#A0AEC0] text-sm font-semibold">Action</th>
  <th className="px-6 py-4 text-left text-[#A0AEC0] text-sm font-semibold">User</th>
  <th className="px-6 py-4 text-left text-[#A0AEC0] text-sm font-semibold">Target</th>
  <th className="px-6 py-4 text-left text-[#A0AEC0] text-sm font-semibold">Timestamp</th>
</tr>
    </thead>
    <tbody>
{filteredLogs.length === 0 ? (
  <tr>
    <td colSpan={4} className="px-6 py-12 text-center text-[#A0AEC0]">
No audit logs found
    </td>
  </tr>
) : (
  filteredLogs.map((log) => (
    <tr
key={log.log_id}
className="border-t border-[rgba(255,255,255,0.05)] hover:bg-[rgba(255,255,255,0.02)] transition-colors"
    >
<td className="px-6 py-4">
  <div className="flex items-center gap-2">
    <span className="text-lg">{getActionIcon(log.action)}</span>
    <span className={`font-mono text-sm ${getActionColor(log.action)}`}>
{log.action}
    </span>
  </div>
</td>
<td className="px-6 py-4">
  <div className="text-white text-sm font-medium">
    {log.user_name || 'Unknown User'}
  </div>
  {log.user_email && (
    <div className="text-[#A0AEC0] text-xs">
{log.user_email}
    </div>
  )}
</td>
<td className="px-6 py-4">
  <div className="text-[#A0AEC0] text-sm">
    <span className="text-[#6366F1] font-semibold">{log.target_type}</span>
    <span className="text-xs ml-2 font-mono">{log.target_id.slice(0, 12)}...</span>
  </div>
</td>
<td className="px-6 py-4">
  <div className="text-[#A0AEC0] text-sm font-mono">
    {formatDate(log.created_at)}
  </div>
</td>
    </tr>
  ))
)}
    </tbody>
  </table>
</div>
    </div>

    {/* Results Count */}
    <div className="mt-4 text-center text-[#A0AEC0] text-sm">
Showing {filteredLogs.length} of {logs.length} events
    </div>
  </div>
    </>
  );
};

export default AuditLogs;
